// =============================================================================
// SIREPPAR - Fases del flujo de RECONOCIMIENTO (frontend)
// =============================================================================
// Espejo de src/validators/datosFormularioReconocimiento.ts del backend.
// La fase vive en expediente.datosFormulario.fase. El estado del expediente NO
// cambia durante el sub-flujo de atributos/documentación.
//
// El "observado" NO es una fase: se deriva de tener observaciones pendientes
// (flujo estándar, tab Observaciones).
// =============================================================================

export const FASES_RECONOCIMIENTO = [
  'WIZARD_EXTERNO_COMPLETO',
  'ATRIBUTOS_PENDIENTES',
  'ATRIBUTOS_A_PUBLICAR',
  'ATRIBUTOS_PUBLICADOS',
  'ATRIBUTOS_EN_SECRETARIO',
  'ATRIBUTOS_EN_TEP',
  'ATRIBUTOS_RECHAZADOS',
  'ATRIBUTOS_APROBADOS',
  'DOCUMENTACION_PENDIENTE',
  'DOCUMENTACION_EN_SECRETARIO',
  'DOCUMENTACION_EN_TEP',
  'DOCUMENTACION_RECHAZADA',
  'DOCUMENTACION_APROBADA',
  // Provisorio → Definitivo (modelado; wizards a implementar más adelante)
  'AFILIADOS_PENDIENTES',
  'AUTORIDADES_PENDIENTES',
  'DEFINITIVO_EN_SECRETARIO',
  'DEFINITIVO_EN_TEP',
  'DEFINITIVO_RECHAZADO',
  'RECONOCIMIENTO_DEFINITIVO',
  // Legacy (datos viejos)
  'EN_ADMISIBILIDAD',
  'EN_ANALISIS',
  'ELEVADO_TRIBUNAL',
  'RECONOCIMIENTO_PROVISORIO',
  'CARGA_AFILIADOS_EN_CURSO',
] as const;

export type FaseReconocimiento = (typeof FASES_RECONOCIMIENTO)[number];

// Etapa de afiliados del reconocimiento provisorio (4 meses para reunir el 4‰ y
// presentar el padrón). Son TRES fases porque significan lo mismo: antes de que
// existiera esta etapa el circuito se detenía en DOCUMENTACION_APROBADA, y los
// expedientes con el provisorio ya otorgado siguen ahí. Todas habilitan las
// mismas acciones — sin esto, un provisorio ya otorgado se queda sin botones.
// Espeja FASES_ETAPA_AFILIADOS del backend (validators/datosFormularioReconocimiento).
export const FASES_ETAPA_AFILIADOS: string[] = [
  'AFILIADOS_PENDIENTES',
  'DOCUMENTACION_APROBADA',
  'CARGA_AFILIADOS_EN_CURSO',  // legacy
];

export const esEtapaAfiliados = (fase?: string | null): boolean =>
  !!fase && FASES_ETAPA_AFILIADOS.includes(fase);

// Etapa de autoridades definitivas del provisorio (elecciones internas + carga de
// autoridades electas y apoderados). Espeja FASES_ETAPA_AUTORIDADES del backend.
export const FASES_ETAPA_AUTORIDADES: string[] = ['AUTORIDADES_PENDIENTES'];

export const esEtapaAutoridades = (fase?: string | null): boolean =>
  !!fase && FASES_ETAPA_AUTORIDADES.includes(fase);

// -----------------------------------------------------------------------------
// Etapas ALCANZADAS (no "etapa en curso")
// -----------------------------------------------------------------------------
// Los documentos obligatorios de una etapa siguen siendo obligatorios DESPUÉS de
// que la etapa termina: el padrón del 4‰ y las actas de las internas son la prueba
// del reconocimiento definitivo, y el Secretario y el TEP los miran justamente
// cuando el trámite ya avanzó.
//
// Con `esEtapaAfiliados` / `esEtapaAutoridades` (que preguntan por la etapa EN
// CURSO) el checklist los soltaba al avanzar y los documentos se caían a "Otros
// documentos". Estos helpers preguntan si la etapa YA SE ALCANZÓ, así que una vez
// que entran al checklist no vuelven a salir.
const FASES_DESPUES_DE_AFILIADOS: string[] = [
  ...FASES_ETAPA_AUTORIDADES,
  'DEFINITIVO_EN_SECRETARIO',
  'DEFINITIVO_EN_TEP',
  'DEFINITIVO_RECHAZADO',
  'RECONOCIMIENTO_DEFINITIVO',
];

/** ¿El trámite llegó (o pasó) por la etapa de afiliados? */
export const alcanzoEtapaAfiliados = (fase?: string | null): boolean =>
  esEtapaAfiliados(fase) || (!!fase && FASES_DESPUES_DE_AFILIADOS.includes(fase));

/** ¿El trámite llegó (o pasó) por la etapa de autoridades definitivas? */
export const alcanzoEtapaAutoridades = (fase?: string | null): boolean =>
  esEtapaAutoridades(fase)
  || (!!fase && ['DEFINITIVO_EN_SECRETARIO', 'DEFINITIVO_EN_TEP', 'DEFINITIVO_RECHAZADO', 'RECONOCIMIENTO_DEFINITIVO'].includes(fase));

// Tramo que va DESDE el reconocimiento provisorio otorgado HASTA que el TEP
// resuelve el definitivo. En todo este tramo el partido tiene únicamente el
// PROVISORIO, aunque el trámite del definitivo ya esté en marcha.
//
// Importa para no mentirle al usuario: el badge del detalle mostraba el nombre de
// la ETAPA del proceso ("Definitivo"), y leído al lado del número de expediente
// parecía decir que el partido ya tenía el reconocimiento definitivo. En este
// tramo se muestra el estado real del expediente y la etapa queda a cargo de la
// barra de proceso, que es donde se lee como "paso 7 de 7".
export const FASES_TRAMO_PROVISORIO: string[] = [
  ...FASES_ETAPA_AFILIADOS,
  ...FASES_ETAPA_AUTORIDADES,
  'DEFINITIVO_EN_SECRETARIO',
  'DEFINITIVO_EN_TEP',
  'DEFINITIVO_RECHAZADO',
  'RECONOCIMIENTO_PROVISORIO',  // legacy
];

export const esTramoProvisorio = (fase?: string | null): boolean =>
  !!fase && FASES_TRAMO_PROVISORIO.includes(fase);

/** Lee la fase actual desde expediente.datosFormulario (o null si no es válida). */
export function getFaseActual(datosFormulario: unknown): FaseReconocimiento | null {
  if (!datosFormulario || typeof datosFormulario !== 'object' || Array.isArray(datosFormulario)) {
    return null;
  }
  const fase = (datosFormulario as Record<string, unknown>).fase;
  return typeof fase === 'string' && (FASES_RECONOCIMIENTO as readonly string[]).includes(fase)
    ? (fase as FaseReconocimiento)
    : null;
}

// Barra de proceso (7 etapas) del ciclo completo de reconocimiento.
export const ETAPAS_RECONOCIMIENTO = [
  'Presentado',
  'Atributos',
  'Documentación',
  'Provisorio',
  'Afiliados',
  'Autoridades',
  'Definitivo',
] as const;

const FASE_A_ETAPA: Record<FaseReconocimiento, number> = {
  WIZARD_EXTERNO_COMPLETO: 0,      // Presentado
  // Atributos
  ATRIBUTOS_PENDIENTES: 1,
  ATRIBUTOS_A_PUBLICAR: 1,
  ATRIBUTOS_PUBLICADOS: 1,
  ATRIBUTOS_EN_SECRETARIO: 1,
  ATRIBUTOS_EN_TEP: 1,
  ATRIBUTOS_RECHAZADOS: 1,
  ATRIBUTOS_APROBADOS: 1,
  // Documentación
  DOCUMENTACION_PENDIENTE: 2,
  DOCUMENTACION_EN_SECRETARIO: 2,
  DOCUMENTACION_EN_TEP: 2,
  DOCUMENTACION_RECHAZADA: 2,
  // Provisorio → Definitivo
  DOCUMENTACION_APROBADA: 3,       // Provisorio
  AFILIADOS_PENDIENTES: 4,         // Afiliados
  AUTORIDADES_PENDIENTES: 5,       // Autoridades
  DEFINITIVO_EN_SECRETARIO: 6,     // Definitivo
  DEFINITIVO_EN_TEP: 6,            // Definitivo
  DEFINITIVO_RECHAZADO: 6,
  RECONOCIMIENTO_DEFINITIVO: 6,
  // Legacy
  EN_ADMISIBILIDAD: 1,
  EN_ANALISIS: 2,
  ELEVADO_TRIBUNAL: 6,
  RECONOCIMIENTO_PROVISORIO: 3,
  CARGA_AFILIADOS_EN_CURSO: 4,
};

export function etapaIndexDeFase(fase: FaseReconocimiento): number {
  return FASE_A_ETAPA[fase] ?? 0;
}

// -----------------------------------------------------------------------------
// Plazo de afiliados del reconocimiento provisorio (4 meses).
// Lo guarda el backend en datosFormulario al registrar la resolución del TEP que
// otorga el provisorio (routes/reconocimiento.ts, tep-otorga-documentacion), y se
// cuenta desde la FECHA DEL ACTA, no desde el registro en el sistema.
// -----------------------------------------------------------------------------
export interface PlazoAfiliados {
  vence: string;            // ISO
  resolucionEn: string | null;
  acta: string | null;
  diasRestantes: number;    // negativo si ya venció
  vencido: boolean;
}

export function getPlazoAfiliados(datosFormulario: unknown): PlazoAfiliados | null {
  if (!datosFormulario || typeof datosFormulario !== 'object' || Array.isArray(datosFormulario)) {
    return null;
  }
  const obj = datosFormulario as Record<string, unknown>;
  const vence = obj.plazoAfiliadosVence;
  if (typeof vence !== 'string') return null;
  const fechaVence = new Date(vence);
  if (isNaN(fechaVence.getTime())) return null;
  // Días de calendario, comparando a medianoche para que "vence hoy" dé 0.
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const limite = new Date(fechaVence);
  limite.setHours(0, 0, 0, 0);
  const diasRestantes = Math.round((limite.getTime() - hoy.getTime()) / 86400000);
  return {
    vence,
    resolucionEn: typeof obj.provisorioResolucionEn === 'string' ? obj.provisorioResolucionEn : null,
    acta: typeof obj.provisorioActa === 'string' ? obj.provisorioActa : null,
    diasRestantes,
    vencido: diasRestantes < 0,
  };
}

/** Texto corto del plazo, para la columna Días de la bandeja y los badges. */
export function textoPlazoAfiliados(p: PlazoAfiliados): string {
  const n = Math.abs(p.diasRestantes);
  const plural = n === 1 ? 'día' : 'días';
  if (p.diasRestantes < 0) return `${n} ${plural} de atraso`;
  if (p.diasRestantes === 0) return 'Vence hoy';
  return `${n} ${plural} restantes`;
}

export type FaseTono = 'pend' | 'info' | 'obs' | 'bad' | 'ok';

const FASE_INFO: Record<FaseReconocimiento, { label: string; tono: FaseTono }> = {
  WIZARD_EXTERNO_COMPLETO: { label: 'Presentado — falta iniciar el trámite', tono: 'pend' },
  ATRIBUTOS_PENDIENTES: { label: 'Atributos — pendientes de revisión', tono: 'pend' },
  ATRIBUTOS_A_PUBLICAR: { label: 'Atributos — en el Secretario (a publicar)', tono: 'info' },
  ATRIBUTOS_PUBLICADOS: { label: 'Atributos — publicados para oposición', tono: 'info' },
  ATRIBUTOS_EN_SECRETARIO: { label: 'Atributos — en el Secretario (para elevar al TEP)', tono: 'info' },
  ATRIBUTOS_EN_TEP: { label: 'Atributos — elevados al TEP', tono: 'info' },
  ATRIBUTOS_RECHAZADOS: { label: 'Atributos — rechazados por el TEP', tono: 'bad' },
  ATRIBUTOS_APROBADOS: { label: 'Atributos — aprobados', tono: 'ok' },
  DOCUMENTACION_PENDIENTE: { label: 'Documentación — pendiente', tono: 'pend' },
  DOCUMENTACION_EN_SECRETARIO: { label: 'Documentación — en revisión del Secretario', tono: 'info' },
  DOCUMENTACION_EN_TEP: { label: 'Documentación — elevada al TEP', tono: 'info' },
  DOCUMENTACION_RECHAZADA: { label: 'Documentación — denegada por el TEP', tono: 'bad' },
  DOCUMENTACION_APROBADA: { label: 'Reconocimiento provisorio otorgado', tono: 'ok' },
  AFILIADOS_PENDIENTES: { label: 'Provisorio — pendiente de afiliados (4 meses)', tono: 'pend' },
  AUTORIDADES_PENDIENTES: { label: 'Provisorio — pendiente de autoridades', tono: 'pend' },
  // "Provisorio — ..." y no "Definitivo — ...": lo que el partido TIENE en esta
  // fase sigue siendo el provisorio; el definitivo está en trámite.
  DEFINITIVO_EN_SECRETARIO: { label: 'Provisorio — en el Secretario (para elevar el definitivo al TEP)', tono: 'info' },
  DEFINITIVO_EN_TEP: { label: 'Definitivo — en el TEP', tono: 'info' },
  DEFINITIVO_RECHAZADO: { label: 'Definitivo — rechazado por el TEP', tono: 'bad' },
  EN_ADMISIBILIDAD: { label: 'En admisibilidad', tono: 'info' },
  EN_ANALISIS: { label: 'En análisis', tono: 'info' },
  ELEVADO_TRIBUNAL: { label: 'Elevado al Tribunal', tono: 'info' },
  RECONOCIMIENTO_PROVISORIO: { label: 'Reconocimiento provisorio', tono: 'ok' },
  CARGA_AFILIADOS_EN_CURSO: { label: 'Carga de afiliados en curso', tono: 'info' },
  RECONOCIMIENTO_DEFINITIVO: { label: 'Reconocimiento definitivo', tono: 'ok' },
};

export function faseLabel(fase: FaseReconocimiento): string {
  return FASE_INFO[fase]?.label ?? fase;
}

export function faseTono(fase: FaseReconocimiento): FaseTono {
  return FASE_INFO[fase]?.tono ?? 'info';
}

// Explicación en texto plano (sin color) de qué significa cada estado.
const FASE_DESCRIPCION: Record<FaseReconocimiento, string> = {
  WIZARD_EXTERNO_COMPLETO: 'El partido presentó la solicitud por el wizard. Falta que la SEP acepte el trámite (Iniciar trámite) y registre el inicio.',
  ATRIBUTOS_PENDIENTES: 'La supervisora analiza los atributos (nombre, sigla, colores, símbolo): homonimia y completitud, y los eleva al Secretario para que los publique.',
  ATRIBUTOS_A_PUBLICAR: 'Atributos verificados y elevados: el Secretario Electoral es quien ordena la publicación para oposiciones (Art. 10).',
  ATRIBUTOS_PUBLICADOS: 'Atributos publicados por el Secretario; corre el plazo de oposiciones (5 días). Luego el supervisor redacta el informe con los resultados para el Secretario.',
  ATRIBUTOS_EN_SECRETARIO: 'El informe al Secretario ya se generó; el Secretario eleva los atributos al Tribunal Electoral.',
  ATRIBUTOS_EN_TEP: 'El secretario eleva los atributos al Tribunal Electoral, que debe resolver (audiencia).',
  ATRIBUTOS_RECHAZADOS: 'El TEP rechazó los atributos. Se espera al partido para que los corrija y se reinicia el ciclo.',
  ATRIBUTOS_APROBADOS: 'El TEP aprobó los atributos. Queda habilitada la carga de la documentación.',
  DOCUMENTACION_PENDIENTE: 'El partido presenta la documentación del Art. 18/19; el analista de la SEP la revisa.',
  DOCUMENTACION_EN_SECRETARIO: 'El Secretario Electoral analiza la documentación antes de elevarla al TEP.',
  DOCUMENTACION_EN_TEP: 'La documentación fue elevada al Tribunal Electoral (audiencia pública).',
  DOCUMENTACION_RECHAZADA: 'El TEP denegó la documentación. Se observa al partido para que la corrija.',
  DOCUMENTACION_APROBADA: 'El TEP otorgó el reconocimiento provisorio. El partido debe reunir afiliados y definir autoridades para el definitivo.',
  AFILIADOS_PENDIENTES: 'Reconocimiento provisorio otorgado. El partido tiene 4 meses desde la resolución del TEP para reunir el 4‰ del padrón electoral y presentar el padrón de afiliados: el Excel que procesa el sistema y el mismo listado impreso y firmado (PDF).',
  AUTORIDADES_PENDIENTES: 'El padrón de afiliados fue verificado. El partido debe convocar elecciones internas y cargar sus autoridades definitivas.',
  DEFINITIVO_EN_SECRETARIO: 'La SEP verificó las autoridades definitivas y elevó su informe; el Secretario Electoral eleva el expediente al Tribunal.',
  DEFINITIVO_EN_TEP: 'El TEP evalúa afiliados y autoridades para resolver el reconocimiento definitivo.',
  DEFINITIVO_RECHAZADO: 'El TEP rechazó el reconocimiento definitivo.',
  EN_ADMISIBILIDAD: 'El expediente está en control de admisibilidad.',
  EN_ANALISIS: 'El expediente está en análisis.',
  ELEVADO_TRIBUNAL: 'El expediente fue elevado al Tribunal Electoral.',
  RECONOCIMIENTO_PROVISORIO: 'El partido obtuvo el reconocimiento provisorio.',
  CARGA_AFILIADOS_EN_CURSO: 'El partido está completando la carga de afiliados exigida por ley.',
  RECONOCIMIENTO_DEFINITIVO: 'El partido obtuvo el reconocimiento definitivo.',
};

export function faseDescripcion(fase: FaseReconocimiento): string {
  return FASE_DESCRIPCION[fase] ?? '';
}

// -----------------------------------------------------------------------------
// Acciones por fase (botones top-right en el detalle del expediente).
// `color`: clase Tailwind de fondo.
// `kind`: 'endpoint' (POST directo) | 'motivo' (abre modal de motivo) | 'obs'
//         (redirige a la tab Observaciones; la observación se crea allí).
// `grupo`: 'revision' (analistas: iniciar/elevar-a-secretario/observar/subsanar)
//          | 'decision' (Secretario: publicar/elevar-al-TEP/registrar TEP).
// El componente filtra por rol: revisión → analistas; decisión → Secretario.
// La SEP NO rechaza: el rechazo/denegación es del TEP y se registra en fase *_EN_TEP.
// -----------------------------------------------------------------------------
export type ReconGrupo = 'revision' | 'decision';

export interface ReconAccion {
  key: string;
  label: string;
  color: string;
  endpoint: string | null;
  kind: 'endpoint' | 'motivo' | 'obs' | 'noPresentado';
  grupo: ReconGrupo;
  // Para kind 'motivo': título y etiqueta del modal de texto (informe / motivo).
  modalTitulo?: string;
  modalLabel?: string;
}

const A = {
  iniciar: { key: 'recon_iniciar', label: 'Iniciar trámite', color: 'bg-blue-600', endpoint: 'iniciar-tramite', kind: 'endpoint', grupo: 'revision' } as ReconAccion,
  // La supervisora VERIFICA los atributos y se los eleva al Secretario, que es quien
  // ordena la publicación (Art. 10). Abre su propio modal (interceptado por endpoint).
  elevarSecAtrib: { key: 'recon_elevar_sec_atrib', label: 'Elevar a Secretario', color: 'bg-indigo-600', endpoint: 'elevar-secretario-atributos', kind: 'endpoint', grupo: 'revision' } as ReconAccion,
  // La publicación es un acto del SECRETARIO (grupo decisión).
  publicar: { key: 'recon_publicar', label: 'Publicar', color: 'bg-blue-600', endpoint: 'publicar-atributos', kind: 'endpoint', grupo: 'decision' } as ReconAccion,
  // El Secretario primero redacta el Informe al Secretario (paso previo), y luego
  // eleva al TEP. Ambas acciones abren su propio modal (interceptadas por endpoint).
  informeSecretario: { key: 'recon_informe_secretario', label: 'Informe al Secretario', color: 'bg-slate-600', endpoint: 'informe-secretario', kind: 'endpoint', grupo: 'decision' } as ReconAccion,
  elevarTepAtrib: { key: 'recon_elevar_tep_atrib', label: 'Elevar al TEP', color: 'bg-indigo-600', endpoint: 'elevar-tep-atributos', kind: 'motivo', grupo: 'decision', modalTitulo: 'Elevar atributos al TEP', modalLabel: 'Informe del Secretario (incluir oposiciones si las hubo)' } as ReconAccion,
  tepApruebaAtrib: { key: 'recon_tep_aprueba_atrib', label: 'TEP aprueba', color: 'bg-green-600', endpoint: 'tep-aprueba-atributos', kind: 'endpoint', grupo: 'decision' } as ReconAccion,
  tepRechazaAtrib: { key: 'recon_tep_rechaza_atrib', label: 'TEP rechaza', color: 'bg-red-600', endpoint: 'tep-rechaza-atributos', kind: 'motivo', grupo: 'decision', modalTitulo: 'Registrar rechazo del TEP', modalLabel: 'Motivo del rechazo' } as ReconAccion,
  elevarSecDoc: { key: 'recon_elevar_sec_doc', label: 'Elevar a Secretario', color: 'bg-indigo-600', endpoint: 'elevar-secretario-documentacion', kind: 'endpoint', grupo: 'revision' } as ReconAccion,
  elevarTepDoc: { key: 'recon_elevar_tep_doc', label: 'Elevar al TEP', color: 'bg-indigo-600', endpoint: 'elevar-tep-documentacion', kind: 'endpoint', grupo: 'decision' } as ReconAccion,
  tepOtorgaDoc: { key: 'recon_tep_otorga_doc', label: 'TEP otorga provisorio', color: 'bg-green-600', endpoint: 'tep-otorga-documentacion', kind: 'endpoint', grupo: 'decision' } as ReconAccion,
  tepDeniegaDoc: { key: 'recon_tep_deniega_doc', label: 'TEP rechaza', color: 'bg-red-600', endpoint: 'tep-deniega-documentacion', kind: 'motivo', grupo: 'decision', modalTitulo: 'Registrar denegación del TEP', modalLabel: 'Motivo de la denegación' } as ReconAccion,
  observar: (reobs: boolean): ReconAccion => ({ key: 'recon_observar', label: reobs ? 'Re-observar' : 'Observar', color: 'bg-orange-600', endpoint: null, kind: 'obs', grupo: 'revision' }),
  // En la etapa de atributos la acción es una INTIMACIÓN (mismo mecanismo que
  // observar: abre el panel y usa el endpoint /observar, que el backend trata
  // como intimación cuando la fase es de atributos).
  intimar: (reint: boolean): ReconAccion => ({ key: 'recon_intimar', label: reint ? 'Re-intimar' : 'Intimar', color: 'bg-orange-600', endpoint: null, kind: 'obs', grupo: 'revision' }),
  aceptarSubsanacion: { key: 'recon_aceptar_subsanacion', label: 'Aceptar subsanación', color: 'bg-green-600', endpoint: 'aceptar-subsanacion', kind: 'endpoint', grupo: 'revision' } as ReconAccion,
  // --- Provisorio → definitivo ---
  // La supervisora APRUEBA los afiliados presentados (los dos documentos del padrón
  // verificados); eso es lo que habilita la etapa de autoridades definitivas. El
  // botón se llama por lo que hace la SEP, no por la fase a la que lleva.
  pasarAutoridades: { key: 'recon_pasar_autoridades', label: 'Aprobar afiliados', color: 'bg-green-600', endpoint: 'pasar-autoridades-pendientes', kind: 'endpoint', grupo: 'revision' } as ReconAccion,
  // Vencido el plazo de 4 meses sin padrón presentado: se eleva el informe al
  // Secretario por el circuito "dado por no presentado" (routes/noPresentado.ts).
  // "No presentado" DEL TRÁMITE: el partido no respondió una intimación de atributos
  // o una observación de documentación, y el plazo venció. Es el mismo circuito que
  // en reempadronamiento (routes/noPresentado.ts) — no el de afiliados/autoridades,
  // que son las etapas del provisorio y tienen su propio texto.
  noPresentadoTramite: { key: 'recon_no_presentado_tramite', label: 'No presentado', color: 'bg-red-700', endpoint: null, kind: 'noPresentado', grupo: 'revision' } as ReconAccion,
  noPresentadoAfiliados: { key: 'recon_no_presentado_afiliados', label: 'No presentado', color: 'bg-red-600', endpoint: null, kind: 'noPresentado', grupo: 'revision' } as ReconAccion,
  // Mismo circuito para la etapa de autoridades definitivas. Se habilita por dos
  // caminos (basta uno): venció el plazo de 4 meses del provisorio, o venció el
  // plazo de una observación de esta etapa sin que el partido respondiera.
  noPresentadoAutoridades: { key: 'recon_no_presentado_autoridades', label: 'No presentado', color: 'bg-red-600', endpoint: null, kind: 'noPresentado', grupo: 'revision' } as ReconAccion,
  // Etapa de autoridades definitivas: mismo circuito de DOS pasos que la etapa de
  // documentación. La SEP revisa lo presentado (autoridades electas + apoderados +
  // los tres documentos) y redacta el informe al Secretario; después el Secretario
  // eleva al TEP. Ambos abren su modal de informe (interceptados por endpoint).
  // El primer botón se deshabilita hasta que los tres documentos estén verificados.
  elevarSecDefinitivo: { key: 'recon_elevar_sec_definitivo', label: 'Elevar a Secretario', color: 'bg-indigo-600', endpoint: 'elevar-secretario-definitivo', kind: 'endpoint', grupo: 'revision' } as ReconAccion,
  elevarTepDefinitivo: { key: 'recon_elevar_tep_definitivo', label: 'Elevar al TEP (definitivo)', color: 'bg-indigo-600', endpoint: 'elevar-tep-definitivo', kind: 'endpoint', grupo: 'decision' } as ReconAccion,
  // Resolución del TEP sobre el definitivo: ambas abren el modal del acta (PDF +
  // N° de acta + fecha), interceptado por endpoint vía RESOLUCIONES_TEP.
  tepApruebaDefinitivo: { key: 'recon_tep_aprueba_definitivo', label: 'TEP otorga definitivo', color: 'bg-green-600', endpoint: 'tep-aprueba-definitivo', kind: 'endpoint', grupo: 'decision' } as ReconAccion,
  tepRechazaDefinitivo: { key: 'recon_tep_rechaza_definitivo', label: 'TEP rechaza', color: 'bg-red-600', endpoint: 'tep-rechaza-definitivo', kind: 'endpoint', grupo: 'decision' } as ReconAccion,
  volverAtras: { key: 'recon_volver_atras', label: 'Volver atrás', color: 'bg-amber-600', endpoint: 'volver-atras', kind: 'endpoint', grupo: 'revision' } as ReconAccion,
  // El SECRETARIO devuelve el expediente un paso atrás en vez de elevarlo, con un
  // informe a la SEP que explica por qué. Distinto de A.volverAtras, que es de la
  // SEP para reabrir el trámite después de un rechazo del TEP.
  devolverSep: { key: 'recon_devolver_sep', label: 'Volver atrás', color: 'bg-amber-600', endpoint: 'devolver-sep', kind: 'endpoint', grupo: 'decision' } as ReconAccion,
};

/**
 * Acciones disponibles según la fase y si hay observaciones pendientes.
 * El filtrado por rol (revisión/decisión) lo hace el componente.
 */
export function accionesReconocimiento(
  fase: FaseReconocimiento,
  hayObsPendiente: boolean,
): ReconAccion[] {
  // "No presentado" se agrega en las fases donde el partido DEBE una respuesta. No
  // lleva condición de plazo acá: quien llama ya filtra por `kind === 'noPresentado'`
  // contra `noPresentadoHabilitado` (plazo de 4 meses vencido, o plazo de una
  // observación vencido sin respuesta). Repetir la condición sería tener dos reglas
  // para lo mismo.
  const noPresentado = [A.noPresentadoTramite];
  switch (fase) {
    case 'WIZARD_EXTERNO_COMPLETO':
      return [A.iniciar];

    // --- Atributos --- (la supervisora verifica y eleva; el Secretario publica y
    // después eleva al TEP)
    case 'ATRIBUTOS_PENDIENTES':
      return hayObsPendiente
        ? [A.intimar(true), A.aceptarSubsanacion, ...noPresentado]
        : [A.elevarSecAtrib, A.intimar(false)];
    // Verificados y en manos del Secretario: publica, o lo devuelve a la SEP.
    case 'ATRIBUTOS_A_PUBLICAR':
      return [A.publicar, A.devolverSep];
    case 'ATRIBUTOS_PUBLICADOS':
      // Publicados: la supervisora redacta el Informe al Secretario (que transiciona
      // a ATRIBUTOS_EN_SECRETARIO). Recién ahí aparece "Elevar al TEP".
      return [A.informeSecretario];
    case 'ATRIBUTOS_EN_SECRETARIO':
      return [A.elevarTepAtrib, A.devolverSep];
    case 'ATRIBUTOS_EN_TEP':
      return [A.tepApruebaAtrib, A.tepRechazaAtrib, A.devolverSep];
    case 'ATRIBUTOS_RECHAZADOS':
      // Tras el rechazo del TEP el trámite queda a la espera de que el PARTIDO
      // corrija los atributos desde su wizard de corrección (única vía). Del lado
      // interno solo se puede intimar (para apurar al partido si demora); cuando
      // el partido corrige, la fase pasa a ATRIBUTOS_PENDIENTES y reaparece Publicar.
      return [A.intimar(hayObsPendiente), ...noPresentado];

    // --- Documentación ---
    case 'DOCUMENTACION_PENDIENTE':
      return hayObsPendiente
        ? [A.observar(true), A.aceptarSubsanacion, ...noPresentado]
        : [A.elevarSecDoc, A.observar(false)];
    case 'DOCUMENTACION_EN_SECRETARIO':
      return hayObsPendiente
        ? [A.observar(true), A.aceptarSubsanacion, A.devolverSep]
        : [A.elevarTepDoc, A.observar(false), A.devolverSep];
    case 'DOCUMENTACION_EN_TEP':
      return [A.tepOtorgaDoc, A.tepDeniegaDoc, A.devolverSep];
    case 'DOCUMENTACION_RECHAZADA':
      return [A.volverAtras, A.observar(hayObsPendiente), ...noPresentado];

    // --- Provisorio → definitivo ---
    // Etapa de afiliados: la supervisora avanza cuando el padrón está verificado
    // (el botón se deshabilita solo hasta entonces), puede observar al partido si
    // lo presentado está incompleto, y —vencidos los 4 meses— darlo por no
    // presentado. El estado sigue siendo REC_PROVISORIO en todo el tramo.
    case 'AFILIADOS_PENDIENTES':
    case 'DOCUMENTACION_APROBADA':
    case 'CARGA_AFILIADOS_EN_CURSO':
      return hayObsPendiente
        ? [A.pasarAutoridades, A.observar(true), A.aceptarSubsanacion, A.noPresentadoAfiliados]
        : [A.pasarAutoridades, A.observar(false), A.noPresentadoAfiliados];

    // Etapa de autoridades definitivas: la supervisora revisa la presentación del
    // partido y eleva al TEP cuando los tres documentos están verificados; también
    // puede observar si lo presentado está incompleto. El estado sigue siendo
    // REC_PROVISORIO en todo el tramo.
    case 'AUTORIDADES_PENDIENTES':
      return hayObsPendiente
        ? [A.elevarSecDefinitivo, A.observar(true), A.aceptarSubsanacion, A.noPresentadoAutoridades]
        : [A.elevarSecDefinitivo, A.observar(false), A.noPresentadoAutoridades];
    case 'DEFINITIVO_EN_SECRETARIO':
      return [A.elevarTepDefinitivo, A.devolverSep];
    // El TEP resuelve el definitivo. El Secretario también puede deshacer la
    // elevación (devolverSep → vuelve a DEFINITIVO_EN_SECRETARIO).
    case 'DEFINITIVO_EN_TEP':
      return [A.tepApruebaDefinitivo, A.tepRechazaDefinitivo, A.devolverSep];

    default:
      return [];
  }
}

/** Filtra acciones por el rol del usuario. */
export function filtrarAccionesPorRol(acciones: ReconAccion[], opts: { esRevisor: boolean; esSecretario: boolean }): ReconAccion[] {
  return acciones.filter((a) =>
    (a.grupo === 'revision' && opts.esRevisor) || (a.grupo === 'decision' && opts.esSecretario),
  );
}
